/* eslint-disable @typescript-eslint/no-empty-interface */
import React from 'react';
import { AllieLoadingConfig, RouteConfigLoading } from '@allie/app-ui-lib';
import { loadScript } from './helpers';

export interface BaseMFEProps extends CommonAttrs {
  exposedPath: string;
  loadingMessage?: string;
  loadingErrorMessage?: string;
  name: string;
  entryPointUrl: string;
  className?: string;
  configLoading?: AllieLoadingConfig;
  routeConfigLoading?: RouteConfigLoading;
}

export type BaseMFEMountParams = CommonAttrs;

export interface BaseMFEMountResponse {
  onParamsChange: any;
}

interface CommonAttrs {}

type MountType<T, R> = (el: Element, params: T) => R;
type UnmountType = (ref: unknown) => void;

interface BaseMFEApp<T, R> {
  mount: MountType<T, R>;
  unmount?: UnmountType;
}

type MyState = {
  isLoading: boolean;
  isError: boolean;
  unmount?: UnmountType;
  update?: any;
};

export abstract class BaseMicroFrontend<T extends BaseMFEProps = any> extends React.Component {
  declare props: T;

  componentRef: React.RefObject<unknown>;
  state: MyState = {
    isLoading: true,
    isError: false,
  };
  constructor(props: T) {
    super(props);
    this.props = props;
    this.componentRef = React.createRef();
  }

  abstract buildMountParams(): BaseMFEMountParams;
  abstract afterMount(response: BaseMFEMountResponse): void;

  afterLoad(app: () => BaseMFEApp<BaseMFEMountParams, BaseMFEMountResponse>) {
    const params = this.buildMountParams();

    const { mount, unmount } = app();
    this.setState({ mount, unmount, isLoading: false });

    const response = mount(this.componentRef.current as Element, params);

    this.setState({ update: response.onParamsChange });
  }

  shouldComponentUpdate(nextProps: any): boolean {
    // Only call onParamsChange if props actually changed
    if (this.hasPropsChanged(this.props, nextProps)) {
      this.state.update?.(nextProps);
    }

    return true;
  }

  private hasPropsChanged(prevProps: any, nextProps: any): boolean {
    // Compare all relevant props for changes
    const prevKeys = Object.keys(prevProps);
    const nextKeys = Object.keys(nextProps);

    if (prevKeys.length !== nextKeys.length) {
      return true;
    }

    for (const key of prevKeys) {
      if (!this.deepEqual(prevProps[key], nextProps[key])) {
        return true;
      }
    }

    return false;
  }

  private deepEqual(a: any, b: any): boolean {
    if (a === b) return true;

    if (a == null || b == null) return a === b;

    if (typeof a !== typeof b) return false;

    if (typeof a === 'function' && typeof b === 'function') {
      return a.toString() === b.toString();
    }

    // For arrays
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (!this.deepEqual(a[i], b[i])) return false;
      }
      return true;
    }

    // For objects
    if (typeof a === 'object' && typeof b === 'object') {
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);

      if (keysA.length !== keysB.length) return false;

      for (const key of keysA) {
        if (!keysB.includes(key)) return false;
        if (!this.deepEqual(a[key], b[key])) return false;
      }
      return true;
    }

    return false;
  }

  async componentDidMount() {
    const { exposedPath, name, entryPointUrl } = this.props;

    try {
      await loadScript(name, entryPointUrl);
      const app = await (window[name as any] as any).get(exposedPath);

      this.afterLoad(app);

      console.log(`${name}-${exposedPath}: componentDidMount`);
    } catch (error) {
      console.log(`${name}-${exposedPath}: Error ${error}`);
      this.setState({ isLoading: false, isError: true });
    }
  }

  componentWillUnmount() {
    const { unmount } = this.state;
    const el = this.componentRef.current;
    if (unmount && el) unmount(el);
  }

  render() {
    const { isError } = this.state;
    const { className, loadingErrorMessage } = this.props;
    return (
      <>
        {isError && (loadingErrorMessage || 'There was a problem loading this section')}
        <div className={className} ref={(node) => ((this.componentRef as any).current = node)} />
      </>
    );
  }
}
